package com.castly.admin.ui.labels

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.BorderRadius
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun LabelsPage(apiClient: ApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    var labels by remember { mutableStateOf<List<LabelDto>>(emptyList()) }
    var selectedLabel by remember { mutableStateOf<LabelDetailDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var showCreateForm by remember { mutableStateOf(false) }
    var includeInactive by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }

    fun loadLabels() {
        scope.launch {
            loading = true
            error = null
            try {
                labels = apiClient.listLabels(includeInactive)
            } catch (e: Exception) {
                error = "Failed to load labels: ${e.message}"
            }
            loading = false
        }
    }

    fun loadLabelDetail(id: Int) {
        scope.launch {
            try {
                selectedLabel = apiClient.getLabel(id)
            } catch (e: Exception) {
                error = "Failed to load label details: ${e.message}"
            }
        }
    }

    LaunchedEffect(includeInactive) {
        loadLabels()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            height(100.percent)
            gap(Spacing.lg)
        }
    }) {
        // Left panel: Label list
        Div({
            style {
                width(360.px)
                minWidth(360.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.md)
            }
        }) {
            // Header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                H2({
                    style {
                        fontSize(Typography.xl)
                        color(Color(colors.text))
                        margin(0.px)
                    }
                }) { Text("Labels") }

                Button({
                    onClick { showCreateForm = !showCreateForm }
                    style {
                        padding(Spacing.sm, Spacing.md)
                        backgroundColor(Color(colors.primary))
                        color(Color.white)
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(13.px)
                    }
                }) { Text(if (showCreateForm) "Cancel" else "+ New Label") }
            }

            // Create form
            if (showCreateForm) {
                CreateLabelForm(apiClient) {
                    showCreateForm = false
                    loadLabels()
                }
            }

            // Include inactive toggle
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.sm)
                }
            }) {
                Input(InputType.Checkbox) {
                    checked(includeInactive)
                    onChange { includeInactive = it.value }
                }
                Span({
                    style { fontSize(13.px); color(Color(colors.textMuted)) }
                }) { Text("Show inactive labels") }
            }

            // Search
            Input(InputType.Text) {
                value(searchQuery)
                onInput { searchQuery = it.value }
                attr("placeholder", "Search labels...")
                style {
                    padding(Spacing.sm)
                    borderRadius(BorderRadius.sm)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    backgroundColor(Color(colors.background))
                    color(Color(colors.text))
                    fontSize(14.px)
                    width(100.percent)
                }
            }

            // Label list
            val filteredLabels = if (searchQuery.isBlank()) labels else labels.filter {
                it.name.contains(searchQuery, ignoreCase = true)
            }
            if (loading) {
                Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)) } }) {
                    Text("Loading labels...")
                }
            } else if (error != null) {
                Div({ style { padding(Spacing.lg); color(Color(colors.danger)) } }) {
                    Text(error!!)
                }
            } else if (filteredLabels.isEmpty()) {
                Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)) } }) {
                    Text(if (labels.isEmpty()) "No labels found. Create one to get started." else "No labels match your search.")
                }
            } else {
                filteredLabels.forEach { label ->
                    val isSelected = selectedLabel?.id == label.id
                    Div({
                        onClick { loadLabelDetail(label.id) }
                        style {
                            padding(Spacing.md)
                            backgroundColor(Color(if (isSelected) colors.primary else colors.surface))
                            color(Color(if (isSelected) "#ffffff" else colors.text))
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        Div {
                            Div({
                                style { fontWeight("600"); fontSize(14.px) }
                            }) { Text(label.name) }
                            Div({
                                style {
                                    fontSize(12.px)
                                    color(Color(if (isSelected) "rgba(255,255,255,0.8)" else colors.textMuted))
                                }
                            }) {
                                Text("${label.audiobookCount} audiobooks · ${label.userCount} users")
                            }
                        }
                        if (!label.isActive) {
                            Span({
                                style {
                                    fontSize(11.px)
                                    padding(2.px, 6.px)
                                    backgroundColor(Color(colors.danger))
                                    color(Color.white)
                                    borderRadius(BorderRadius.sm)
                                }
                            }) { Text("Inactive") }
                        }
                    }
                }
            }
        }

        // Right panel: Label detail
        Div({
            style {
                flexGrow(1)
                minWidth(0.px)
            }
        }) {
            if (selectedLabel != null) {
                LabelDetailView(
                    label = selectedLabel!!,
                    apiClient = apiClient,
                    onUpdated = {
                        loadLabels()
                        loadLabelDetail(selectedLabel!!.id)
                    }
                )
            } else {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        height(100.percent)
                        color(Color(colors.textMuted))
                        fontSize(14.px)
                    }
                }) {
                    Text("Select a label to view details")
                }
            }
        }
    }
}

@Composable
private fun CreateLabelForm(apiClient: ApiClient, onCreated: () -> Unit) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var creating by remember { mutableStateOf(false) }
    var formError by remember { mutableStateOf<String?>(null) }

    Div({
        style {
            padding(Spacing.md)
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.md)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.sm)
            border(1.px, LineStyle.Solid, Color(colors.border))
        }
    }) {
        Input(InputType.Text) {
            value(name)
            placeholder("Label name")
            onInput { name = it.value }
            style {
                padding(Spacing.sm)
                borderRadius(BorderRadius.sm)
                border(1.px, LineStyle.Solid, Color(colors.border))
                backgroundColor(Color(colors.background))
                color(Color(colors.text))
                fontSize(14.px)
                width(100.percent)
            }
        }

        Input(InputType.Text) {
            value(description)
            placeholder("Description (optional)")
            onInput { description = it.value }
            style {
                padding(Spacing.sm)
                borderRadius(BorderRadius.sm)
                border(1.px, LineStyle.Solid, Color(colors.border))
                backgroundColor(Color(colors.background))
                color(Color(colors.text))
                fontSize(14.px)
                width(100.percent)
            }
        }

        if (formError != null) {
            Div({ style { color(Color(colors.danger)); fontSize(12.px) } }) { Text(formError!!) }
        }

        Button({
            onClick {
                if (name.isBlank()) {
                    formError = "Name is required"
                    return@onClick
                }
                scope.launch {
                    creating = true
                    formError = null
                    try {
                        apiClient.createLabel(CreateLabelRequest(
                            name = name.trim(),
                            description = description.trim().takeIf { it.isNotBlank() }
                        ))
                        name = ""
                        description = ""
                        onCreated()
                    } catch (e: Exception) {
                        formError = e.message ?: "Failed to create label"
                    }
                    creating = false
                }
            }
            style {
                padding(Spacing.sm, Spacing.md)
                backgroundColor(Color(colors.primary))
                color(Color.white)
                border(0.px)
                borderRadius(BorderRadius.md)
                cursor("pointer")
                fontSize(13.px)
            }
        }) { Text(if (creating) "Creating..." else "Create Label") }
    }
}
